import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { Box, Text, Spinner, useToast } from '@chakra-ui/react';
import { authApi } from '../services/api/auth';
import { signInWithCustomToken } from 'firebase/auth';
import { CollabStatus, CollabInviteTokenErrorDto } from '@foodfluence/shared';
import { getCollabInviteErrorMessage, getCollabRedirectPath } from '../utils/authUtils';
import { ff } from '../theme';

const CollabInviteRedirect: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  // Verify token and handle all logic in one effect
  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      toast({
        title: "Invite Link Error",
        description: 'Invalid invite link - missing token',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate('/new-signup', { replace: true });
      return;
    }

    const verifyAndSignIn = async () => {
      try {
        // Verify token on backend and get custom token
        const response = await authApi.verifyCollabInviteToken(token);
        
        if (!response.customToken) {
          throw new Error('No custom token received from server');
        }

        // Store token and collab data for phone verification later
        sessionStorage.setItem('collabInviteToken', token);
        sessionStorage.setItem('collabId', response.collabId);

        // Sign in with custom token immediately
        await signInWithCustomToken(auth, response.customToken);

        // Redirect to collab page
        const redirectPath = getCollabRedirectPath(response.collabId, response.collabStatus);
        navigate(redirectPath, { replace: true });
      } catch (err) {
        const error = err as CollabInviteTokenErrorDto;
        console.error('Token verification/sign-in error:', error);
        
        // Check current auth state at time of error
        const currentUser = auth.currentUser;
        console.log('currentUser', currentUser);
        
        // If user is already signed in, redirect them to the collab
        if (currentUser) {
          const collabId = error.collabId || sessionStorage.getItem('collabId');
          const redirectPath = collabId 
            ? getCollabRedirectPath(collabId, error.collabStatus)
            : '/collabs';
          navigate(redirectPath, { replace: true });
          return;
        }
        
        // Show error toast
        toast({
          title: "Invite Link Error",
          description: getCollabInviteErrorMessage(error),
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        // Redirect based on error type
        const redirectPath = error.tokenAlreadyUsed && error.hasPhoneNumber
          ? '/returning-user'
          : `/new-signup?email=${encodeURIComponent(error.email || '')}`;
        
        navigate(redirectPath, { 
          replace: true,
          state: { email: error.email }
        });
      }
    };

    verifyAndSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Show loading state while verifying token
  return (
    <Box
      height="100vh"
      width="100vw"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="white"
      flexDirection="column"
      gap={4}
    >
      <Spinner size="xl" color={ff.ink} thickness="3px" emptyColor={ff.line} />
      <Text fontSize="15px" color={ff.text2}>Preparing your collaboration invite…</Text>
    </Box>
  );
};

export default CollabInviteRedirect;
